/**
 * Item editor — the item's own fields plus its components.
 *
 * Functional spec: the edit dialogs in `qt_poc/catalog_qt.py`.
 *
 * Two rules the server enforces and this screen has to respect:
 *
 *   1. **A save replaces the whole component list.** `ItemDAO.save` diffs what
 *      it is given against what is stored and deletes the difference, so the
 *      form must always submit every component — including the ones the user
 *      did not touch. There is no partial update.
 *   2. **A SKU may not repeat within one item.** The DAO would silently drop
 *      the duplicate and the API refuses the payload outright, so it is
 *      caught here first and shown next to the offending row rather than as an
 *      opaque 400 after the user hits save.
 */
import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  ActionIcon, Alert, Box, Button, Checkbox, Group, NumberInput, Select,
  Stack, Table, Text, TextInput, Textarea, Tooltip,
} from '@mantine/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'

import { ApiError, api } from '../api/client'
import type { Component, Item, ItemDetail, MasterData } from '../api/types'
import { Modal } from '../components/AppModal'
import { STICKY_END, STICKY_START } from '../components/DataGrid'
import { CODE_STYLE } from '../theme'

const SALE_STATUSES = ['ACTIVE', 'EOS', 'EOL']
const COMPONENT_TYPES = ['Hardware', 'License', 'Support', 'Accessories']

function blankComponent(): Component {
  return {
    id: null, item_id: null, sku: '', internal_sku: '', description: '',
    component_type: 'Hardware', default_qty_ratio: 1, unit_price_usd: 0,
    discount_pct: 0, sale_status: 'ACTIVE', notes: '', term_months: null,
    discount_by_term: false,
  }
}

function blankItem(): Item {
  return {
    id: null, name: '', manufacturer: '', family: '', sale_status: 'ACTIVE',
    notes: '', tags: '', min_stock: 0, components: [blankComponent()],
    created_at: '', updated_at: '', terms_available: [], is_termed: false,
  }
}

/** "ללא תקופה" for a term-less component, else "N חודשים" — same convention
 * the term Select on BomDetail/ReplacementPlanEditor uses for its options,
 * kept as a heading here rather than a selectable value (DP-11: there is no
 * control to EDIT a component's term in this screen, desktop or web — it is
 * importer-derived, same as qt_poc/catalog_qt.py's own item editor). */
function termGroupLabel(term: number | null): string {
  return term == null ? 'ללא תקופה' : `${term} חודשים`
}

interface Props {
  opened: boolean
  onClose: () => void
  /** null = create. */
  itemId: number | null
  master?: MasterData
}

export function ItemEditor({ opened, onClose, itemId, master }: Props) {
  const qc = useQueryClient()
  const [draft, setDraft] = useState<Item>(blankItem)
  const [error, setError] = useState<string | null>(null)

  const existing = useQuery({
    queryKey: ['item', itemId],
    queryFn: () => api.get<ItemDetail>(`/items/${itemId}`),
    enabled: opened && itemId != null,
  })

  useEffect(() => {
    if (!opened) return
    if (itemId == null) setDraft(blankItem())
    else if (existing.data) setDraft(structuredClone(existing.data) as Item)
    setError(null)
  }, [opened, itemId, existing.data])

  // Duplicate SKUs, computed as the user types so the offending rows can be
  // marked rather than surfacing as a 400 after submit.
  const duplicateSkus = useMemo(() => {
    const seen = new Set<string>()
    const dupes = new Set<string>()
    for (const c of draft.components) {
      const sku = c.sku.trim()
      if (!sku) continue
      if (seen.has(sku)) dupes.add(sku)
      seen.add(sku)
    }
    return dupes
  }, [draft.components])

  // Components grouped by term for display (DP-11): term-less first, then
  // ascending by term_months. Groups hold the component's INDEX into
  // draft.components, not a copy of the row, so update/delete still target
  // the real array position — the grouping is a view, not a reshuffle.
  const groupedComponents = useMemo(() => {
    const groups = new Map<number | string, number[]>()
    draft.components.forEach((c, index) => {
      const key = c.term_months ?? 'none'
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(index)
    })
    const keys = [...groups.keys()].sort((a, b) => {
      if (a === 'none') return -1
      if (b === 'none') return 1
      return (a as number) - (b as number)
    })
    return keys.map((key) => ({
      term: key === 'none' ? null : (key as number),
      indices: groups.get(key)!,
    }))
  }, [draft.components])

  const save = useMutation({
    mutationFn: (item: Item) =>
      item.id == null
        ? api.post<Item>('/items', item)
        : api.put<Item>(`/items/${item.id}`, item),
    onSuccess: (saved) => {
      // Invalidate rather than patch the cache: the list rows carry derived
      // columns (dealer names, price context) the server computes, so a
      // locally-patched row would disagree with the next refetch.
      void qc.invalidateQueries({ queryKey: ['items'] })
      void qc.invalidateQueries({ queryKey: ['item', saved.id] })
      void qc.invalidateQueries({ queryKey: ['master-data'] })
      notifications.show({ message: 'הפריט נשמר', color: 'green' })
      onClose()
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'שמירת הפריט נכשלה.'),
  })

  function update(patch: Partial<Item>) {
    setDraft((d) => ({ ...d, ...patch }))
  }

  function updateComponent(index: number, patch: Partial<Component>) {
    setDraft((d) => ({
      ...d,
      components: d.components.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    }))
  }

  function submit() {
    setError(null)
    if (!draft.name.trim()) return setError('יש להזין שם / מק"ט לפריט.')
    if (!draft.manufacturer.trim()) return setError('יש לבחור יצרן.')
    if (duplicateSkus.size)
      return setError(`מק"ט מופיע יותר מפעם אחת: ${[...duplicateSkus].join(', ')}`)

    // Blank rows are dropped rather than rejected — an empty trailing row is
    // how people leave a grid, not an error worth blocking a save over.
    const components = draft.components.filter((c) => c.sku.trim() || c.description.trim())
    save.mutate({ ...draft, components })
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={itemId == null ? 'פריט חדש' : `עריכת פריט — ${draft.name}`}
      size="xl"
    >
      <Stack>
        {error && <Alert color="red" variant="light">{error}</Alert>}

        <Group grow align="flex-start">
          <TextInput
            label='שם / מק"ט יצרן'
            required
            value={draft.name}
            onChange={(e) => update({ name: e.currentTarget.value })}
            styles={{ input: CODE_STYLE }}
          />
          <Select
            label="יצרן"
            required
            searchable
            data={master?.manufacturers ?? []}
            value={draft.manufacturer || null}
            onChange={(v) => update({ manufacturer: v ?? '' })}
          />
        </Group>

        <Group grow align="flex-start">
          <Select
            label="משפחה"
            searchable
            clearable
            clearButtonProps={{ "aria-label": "נקה", size: "md" }}
            data={master?.families ?? []}
            value={draft.family || null}
            onChange={(v) => update({ family: v ?? '' })}
          />
          <Select
            label="סטטוס"
            data={SALE_STATUSES}
            value={draft.sale_status || 'ACTIVE'}
            onChange={(v) => update({ sale_status: v ?? 'ACTIVE' })}
          />
          <TextInput
            label="תגיות"
            placeholder="מופרדות בפסיק"
            value={draft.tags}
            onChange={(e) => update({ tags: e.currentTarget.value })}
          />
          {/* Emergency stock kept on the shelf (schema 4 / replacement
              planning, DP-10) — the replacement-plan engine applies this
              automatically as a line's default reserve unless the plan
              overrides it per line. */}
          <NumberInput
            label="מלאי מינימום"
            hideControls
            min={0}
            decimalScale={2}
            value={draft.min_stock}
            onChange={(v) => update({ min_stock: Number(v) || 0 })}
          />
        </Group>

        <Textarea
          label="הערות"
          autosize
          minRows={2}
          value={draft.notes}
          onChange={(e) => update({ notes: e.currentTarget.value })}
        />

        <Group justify="space-between" mt="sm">
          <Text fw={600}>רכיבים</Text>
          <Button
            size="xs"
            variant="light"
            onClick={() => setDraft((d) => ({ ...d, components: [...d.components, blankComponent()] }))}
          >
            הוסף רכיב
          </Button>
        </Group>

        <Text size="xs" c="dimmed">
          שמירה מחליפה את כל רשימת הרכיבים — רכיב שיוסר מכאן יימחק מהפריט.
        </Text>

        {/* 9 columns in a modal that can be as narrow as a phone screen —
            without a declared minimum width the table shrinks to fit instead
            of scrolling, which is exactly how `default_qty_ratio` ("יחס")
            measured a 23px-wide input (uxaudit.mjs's item-editor probe).
            Same fix as ReplacementPlanEditor.tsx's lines table, same reason. */}
        <Box style={{ overflowX: 'auto' }}>
        <Table withColumnBorders miw={980}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ ...STICKY_START, zIndex: 3 }}>מקט יצרן</Table.Th>
              <Table.Th>מקט פנימי</Table.Th>
              <Table.Th>תיאור</Table.Th>
              <Table.Th>סוג</Table.Th>
              <Table.Th w={90}>יחס</Table.Th>
              <Table.Th w={120}>GPL</Table.Th>
              <Table.Th w={110}>הנחה</Table.Th>
              <Table.Th w={130}>
                {/* DP-11 — bound to Component.discount_by_term. There is no
                    control to edit a component's OWN term here (term_months
                    is importer-derived, same as the desktop editor); the
                    group headings below show it read-only. */}
                הנחה לפי תקופה
              </Table.Th>
              <Table.Th style={{ ...STICKY_END, width: 40, zIndex: 3 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {groupedComponents.map((group) => (
              <Fragment key={group.term ?? 'none'}>
                {/* A heading row, not a second header — only components of a
                    termed item (import, so far) ever produce more than one
                    group; an untermed item's whole list sits quietly under
                    one "ללא תקופה" row that nobody has to act on. */}
                <Table.Tr>
                  <Table.Td
                    colSpan={9}
                    style={{
                      background: 'var(--mantine-color-gray-1)',
                      fontWeight: 600,
                    }}
                  >
                    {termGroupLabel(group.term)}
                  </Table.Td>
                </Table.Tr>
                {group.indices.map((index) => {
                  const component = draft.components[index]
                  return (
                    <Table.Tr key={index}>
                      <Table.Td style={{ ...STICKY_START, zIndex: 1 }}>
                        <TextInput
                          size="xs"
                          value={component.sku}
                          error={duplicateSkus.has(component.sku.trim()) ? 'כפול' : undefined}
                          onChange={(e) => updateComponent(index, { sku: e.currentTarget.value })}
                          styles={{ input: CODE_STYLE }}
                        />
                      </Table.Td>
                      <Table.Td>
                        <TextInput
                          size="xs"
                          value={component.internal_sku}
                          onChange={(e) => updateComponent(index, { internal_sku: e.currentTarget.value })}
                          styles={{ input: CODE_STYLE }}
                        />
                      </Table.Td>
                      <Table.Td>
                        <TextInput
                          size="xs"
                          value={component.description}
                          onChange={(e) => updateComponent(index, { description: e.currentTarget.value })}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Select
                          size="xs"
                          data={COMPONENT_TYPES}
                          value={component.component_type || 'Hardware'}
                          onChange={(v) => updateComponent(index, { component_type: v ?? 'Hardware' })}
                        />
                      </Table.Td>
                      <Table.Td>
                        <NumberInput
                          size="xs"
                          hideControls
                          min={0}
                          decimalScale={2}
                          value={component.default_qty_ratio}
                          onChange={(v) => updateComponent(index, { default_qty_ratio: Number(v) || 0 })}
                        />
                      </Table.Td>
                      <Table.Td>
                        <NumberInput
                          size="xs"
                          hideControls
                          min={0}
                          decimalScale={2}
                          thousandSeparator=","
                          // `?? 0` guards a type that is `number | null` because a
                          // viewer's copy of this field is null (DP-01). This modal
                          // is unreachable for a viewer — Catalog.tsx only opens it
                          // from `canEdit`-gated buttons — so in practice this is
                          // always a real number, same as `blankComponent()` below.
                          value={component.unit_price_usd ?? 0}
                          onChange={(v) => updateComponent(index, { unit_price_usd: Number(v) || 0 })}
                        />
                      </Table.Td>
                      <Table.Td>
                        {/* Stored as a fraction (0.32 = 32%), shown as a percentage —
                            the same convention the desktop grid uses. */}
                        <NumberInput
                          size="xs"
                          hideControls
                          min={0}
                          max={100}
                          suffix="%"
                          decimalScale={2}
                          value={Number((component.discount_pct * 100).toFixed(2))}
                          onChange={(v) => updateComponent(index, { discount_pct: (Number(v) || 0) / 100 })}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Tooltip
                          label="ההנחה בפועל נגזרת לפי התקופה שנבחרה ב-BOM, ולא קבועה כאן"
                          disabled={!component.discount_by_term}
                        >
                          <Checkbox
                            aria-label="הנחה לפי תקופה"
                            size="md"
                            checked={component.discount_by_term}
                            onChange={(e) =>
                              updateComponent(index, { discount_by_term: e.currentTarget.checked })
                            }
                          />
                        </Tooltip>
                      </Table.Td>
                      <Table.Td style={{ ...STICKY_END, zIndex: 2 }}>
                        <ActionIcon
                          size={30}
                          color="red"
                          variant="subtle"
                          aria-label="מחק רכיב"
                          onClick={() =>
                            setDraft((d) => ({
                              ...d,
                              components: d.components.filter((_, i) => i !== index),
                            }))
                          }
                        >
                          ✕
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Fragment>
            ))}
          </Table.Tbody>
        </Table>
        </Box>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>ביטול</Button>
          <Button onClick={submit} loading={save.isPending}>שמור</Button>
        </Group>
      </Stack>
    </Modal>
  )
}
